// quill-init.ts — QUILL EDITOR INITIALIZATION + CODE-BLOCK RICH-CONTENT PATCH, carved out of template.js and
// TS-ported for Phase 2a of plans/bundler-script-load-order-phase2.md — a member of the post-template-forms
// aggregate (footer ~818, between fantasy-cipher and the turn-widget singleton).
//
// MODULE-SCOPE work at eval: ONE typeof-guarded `applyQuillCodeBlockRichContentPatch()` + the FateTide
// attach. Player hosts defer the vendor assets until their first editor; other pages load them eagerly.
// No template.js module-scope reads. initializeQuillEditors' other deps
// (BASE_URL/ftDebugLog/ucwordsAndSpace, ftUpdateInventoryDescriptionScriptPreview) are call-time.
//
// PUBLIC GLOBALS (window-attached): initializeQuillEditors (called from template.js DOMContentLoaded +
// character-creator/inventory-edit-form/creature-actions bundles) and applyQuillCodeBlockRichContentPatch
// (kept as BOTH the implicit window global AND window.FateTide.applyQuillCodeBlockRichContentPatch — modify_source.php).

import { loadQuillAssets } from './lazy-assets';

declare global {
    interface Window {
        FateTide: {
            applyQuillCodeBlockRichContentPatch?: typeof applyQuillCodeBlockRichContentPatch;
        };
        initializeQuillEditors: typeof initializeQuillEditors;
        applyQuillCodeBlockRichContentPatch: typeof applyQuillCodeBlockRichContentPatch;
        ftUpdateInventoryDescriptionScriptPreview?: (invForm: Element) => void;
    }
}

/**
 * Quill's default code block only allows text (no inline formats) and a fixed inner line shape.
 * Widen CodeBlock.allowedChildren (bold/italic/link/etc. like normal paragraphs) and
 * CodeBlockContainer.allowedChildren (headers, p, blockquote, list blocks) so the panel
 * can hold rich text like the [blocktext] / ft-parsed-blocktext path in parse_text.
 * Idempotent; run before any `new Quill` (e.g. modify_source's editor).
 */
function applyQuillCodeBlockRichContentPatch(): void {
    if (typeof Quill === 'undefined') {
        return;
    }
    const quillApi = Quill as unknown as FtQuillConstructor;
    if (quillApi.__ftCodeBlockRichPatched) return;
    try {
        const CodeBlock = quillApi.import('formats/code-block') as FtQuillBlotConstructor | null;
        if (!CodeBlock || !Array.isArray(CodeBlock.allowedChildren)) {
            return;
        }
        const CCB = CodeBlock.requiredContainer;
        if (CCB && Array.isArray(CCB.allowedChildren)) {
            const containerChildren = CCB.allowedChildren;
            const Block = quillApi.import('blots/block') as FtQuillBlotConstructor | null;
            const Header = quillApi.import('formats/header') as FtQuillBlotConstructor | null;
            const Blockquote = quillApi.import('formats/blockquote') as FtQuillBlotConstructor | null;
            const ListItem = quillApi.import('formats/list') as FtQuillBlotConstructor | null;
            const ListContainer = ListItem && ListItem.requiredContainer;
            [Header, Block, Blockquote, ListContainer].forEach((Blot) => {
                if (Blot && containerChildren.indexOf(Blot) < 0) {
                    containerChildren.push(Blot);
                }
            });
        }
        const blockLevelToMerge = quillApi.import('blots/block') as FtQuillBlotConstructor | null;
        const extraInlinePaths = [
            'formats/bold',
            'formats/italic',
            'formats/underline',
            'formats/strike',
            'formats/link',
            'formats/code',
            'formats/script',
        ];
        const merged = new Set(CodeBlock.allowedChildren);
        extraInlinePaths.forEach((path) => {
            try {
                const P = quillApi.import(path);
                if (P) {
                    merged.add(P);
                }
            } catch (e) { /* missing optional format */ }
        });
        if (blockLevelToMerge && Array.isArray(blockLevelToMerge.allowedChildren)) {
            blockLevelToMerge.allowedChildren.forEach((B) => merged.add(B));
        }
        CodeBlock.allowedChildren = Array.from(merged);
        quillApi.__ftCodeBlockRichPatched = true;
    } catch (e) {
        console.error('FateTide: applyQuillCodeBlockRichContentPatch failed', e);
    }
}

if (typeof Quill !== 'undefined') {
    applyQuillCodeBlockRichContentPatch();
}
window.FateTide = window.FateTide || {};
window.FateTide.applyQuillCodeBlockRichContentPatch = applyQuillCodeBlockRichContentPatch;

interface MentionOption {
    id: string;
    /**
     * ⭐ THE TOKEN PREFIX, and it is not always an item_type. For the universal item catalog it is
     * the item's own type ('weapon', 'spell'…); for the CONTAINED kinds it is the kind itself
     * ('article' | 'encounter_creature' | 'inventory'). insertMention() writes it verbatim into
     * `[Name](prefix:id)`, and ft_expand_mentions() reads it back to know which table to resolve.
     */
    item_type: string;
    name: string;
    icon: string;
    /** Friendly kicker shown in the dropdown ("Deity", "Creature in play"). Optional. */
    label?: string;
}

function mentionOptionOf(value: unknown): MentionOption | null {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
    const row = value as Record<string, unknown>;
    if (row.id == null || row.item_type == null || row.name == null || row.icon == null) return null;
    return {
        id: String(row.id),
        item_type: String(row.item_type),
        name: String(row.name),
        icon: String(row.icon),
        label: row.label == null ? undefined : String(row.label),
    };
}

// =============================================================================
// QUILL EDITOR INITIALIZATION
// =============================================================================
let pendingMount: Promise<void> | null = null;
let pendingUnfocusInputs = true;

function initializeQuillEditors(unfocus_inputs: boolean = true): void {
    const editors = document.querySelectorAll('.quill-editor:not(.quill-initialized)');
    if (editors.length === 0) return;
    const assets = loadQuillAssets();
    if (assets) {
        pendingUnfocusInputs = unfocus_inputs;
        pendingMount ??= assets.then(() => {
            pendingMount = null;
            initializeQuillEditors(pendingUnfocusInputs);
        }, error => {
            pendingMount = null;
            console.error('FateTide: editor assets failed to load', error);
        });
        return;
    }
    applyQuillCodeBlockRichContentPatch();
    const quillApi = Quill as unknown as FtQuillConstructor;

    // Ensure Keyboard module is imported
    quillApi.import('modules/keyboard');

    // Register quill-table-better module globally (CDN exposes QuillTableBetter)
    if (typeof QuillTableBetter !== 'undefined') {
        quillApi.register('modules/table-better', QuillTableBetter, true);
    }

    if (editors.length > 0) {
        editors.forEach((editorNode) => {
            const editor = editorNode as HTMLElement;
            if (editor.classList.contains('quill-initialized')) {
                return;
            }

            const layerTypeName = editor.dataset.layerTypeName;
            const hiddenTextarea = document.getElementById(layerTypeName || '') as HTMLInputElement | HTMLTextAreaElement | null;
            const callbackFunctionName = editor.dataset.updateCallback;

            // Initialize Quill editor
            const quill = new quillApi(editor, {
                theme: 'snow',
                modules: {
                    toolbar: [
                        ['bold', 'italic', 'underline', 'strike'],
                        ['blockquote', 'code-block'],
                        [{ header: 1 }, { header: 2 }, { header: 3 }, { header: 4 }, { header: 5 }],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        ['clean'],
                        ['table-better']  // Table insert button in the top toolbar
                    ],
                    'table-better': {
                        toolbarTable: true  // Shows extra buttons when table is focused
                    },
                    keyboard: {
                        bindings: {
                            // Header 1: Ctrl + 1
                            'header 1': {
                                key: '1',
                                shortKey: true,
                                handler: function (range: FtQuillRange, context: FtQuillFormatContext) {
                                    const currentHeader = context.format.header;
                                    quill.formatLine(range, 'header', currentHeader === 1 ? false : 1, quillApi.sources.USER);
                                    quill.setSelection(range);
                                    return false;
                                }
                            },
                            // Header 2: Ctrl + 2
                            'header 2': {
                                key: '2',
                                shortKey: true,
                                handler: function (range: FtQuillRange, context: FtQuillFormatContext) {
                                    const currentHeader = context.format.header;
                                    quill.formatLine(range, 'header', currentHeader === 2 ? false : 2, quillApi.sources.USER);
                                    quill.setSelection(range);
                                    return false;
                                }
                            },
                            // Header 3: Ctrl + 3
                            'header 3': {
                                key: '3',
                                shortKey: true,
                                handler: function (range: FtQuillRange, context: FtQuillFormatContext) {
                                    const currentHeader = context.format.header;
                                    quill.formatLine(range, 'header', currentHeader === 3 ? false : 3, quillApi.sources.USER);
                                    quill.setSelection(range);
                                    return false;
                                }
                            },
                            // Header 4: Ctrl + 4
                            'header 4': {
                                key: '4',
                                shortKey: true,
                                handler: function (range: FtQuillRange, context: FtQuillFormatContext) {
                                    const currentHeader = context.format.header;
                                    quill.formatLine(range, 'header', currentHeader === 4 ? false : 4, quillApi.sources.USER);
                                    quill.setSelection(range);
                                    return false;
                                }
                            },
                            // Header 5: Ctrl + 5 (Corrected)
                            'header 5': {
                                key: '5',
                                shortKey: true,
                                handler: function (range: FtQuillRange, context: FtQuillFormatContext) {
                                    const currentHeader = context.format.header;
                                    quill.formatLine(range, 'header', currentHeader === 5 ? false : 5, quillApi.sources.USER);
                                    quill.setSelection(range);
                                    return false;
                                }
                            },
                            // Ordered List: Ctrl + L
                            'ordered list': {
                                key: 'L',
                                shortKey: true,
                                handler: function (range: FtQuillRange, context: FtQuillFormatContext) {
                                    const currentList = context.format.list;
                                    quill.formatLine(range, 'list', currentList === 'ordered' ? false : 'ordered', quillApi.sources.USER);
                                    quill.setSelection(range);
                                    return false;
                                }
                            },
                            // Bullet List: Ctrl + B
                            'bullet list': {
                                key: 'B',
                                shortKey: true,
                                handler: function (range: FtQuillRange, context: FtQuillFormatContext) {
                                    const currentList = context.format.list;
                                    quill.formatLine(range, 'list', currentList === 'bullet' ? false : 'bullet', quillApi.sources.USER);
                                    quill.setSelection(range);
                                    return false;
                                }
                            },
                            // Merge table-better's keyboard bindings (critical for Tab, Enter, etc. in tables)
                            ...QuillTableBetter.keyboardBindings
                        }
                    }
                }
            });

            // Mark this editor as initialized
            editor.classList.add('quill-initialized');

            // Immediately blur the editor to prevent auto-focus
            quill.blur();

            // Prevent auto-focus
            editor.addEventListener('mousedown', () => {
                setTimeout(() => {
                    quill.focus();
                }, 0);
            });

            // Set the initial content - FIXED FOR TABLE SUPPORT
            const initialContent = editor.dataset.initialContent || '';

            if (initialContent.trim() !== '') {
                // Convert HTML to Delta and apply via updateContents (preserves tables)
                const delta = quill.clipboard.convert({ html: initialContent });

                // Clear empty initial content first
                quill.setContents([]); // or quill.deleteText(0, quill.getLength());

                // Apply the converted delta
                quill.updateContents(delta, quillApi.sources.SILENT);

                // Position cursor at the end
                quill.setSelection(quill.getLength(), 0, quillApi.sources.SILENT);
            }

            // Debounce function
            function debounce<TArgs extends unknown[]>(func: (...args: TArgs) => void, delay: number) {
                let timeout: number;
                return function (...args: TArgs) {
                    clearTimeout(timeout);
                    timeout = setTimeout(() => func(...args), delay);
                };
            }

            // Sync Quill content to the textarea on text change (as backup, without callback)
            quill.on('text-change', debounce(function () {
                if (hiddenTextarea) {
                    hiddenTextarea.value = quill.root.innerHTML;
                    ftDebugLog('quill', { debouncedSync: true, valueLen: hiddenTextarea.value?.length });
                }
                const callback: unknown = callbackFunctionName
                    ? Reflect.get(window, callbackFunctionName)
                    : undefined;
                if (typeof callback === 'function') {
                    const syntheticEvent = { target: editor };
                    try {
                        callback(syntheticEvent);
                        ftDebugLog('quill', { debouncedCallback: callbackFunctionName });
                    } catch (e) {
                        console.error(`Error calling ${callbackFunctionName}:`, e);
                    }
                }
            }, 1000));

            if (layerTypeName === 'inventory_description') {
                const invForm = editor.closest('.inventory-container-form');
                if (invForm && typeof window.ftUpdateInventoryDescriptionScriptPreview === 'function') {
                    const previewDebounce = debounce(function () {
                        window.ftUpdateInventoryDescriptionScriptPreview!(invForm);
                    }, 300);
                    quill.on('text-change', previewDebounce);
                    setTimeout(previewDebounce, 400);
                }
            }

            // Custom mention dropdown container
            const mentionContainer = document.createElement('div');
            mentionContainer.className = 'custom-mention-container';
            mentionContainer.style.position = 'absolute';
            mentionContainer.style.display = 'none';
            mentionContainer.style.backgroundColor = '#fff';
            mentionContainer.style.border = '1px solid #ccc';
            mentionContainer.style.boxShadow = '0px 4px 6px rgba(0, 0, 0, 0.1)';
            mentionContainer.style.borderRadius = '4px';
            mentionContainer.style.zIndex = '20010';
            mentionContainer.style.maxHeight = '200px';
            mentionContainer.style.overflowY = 'auto';
            mentionContainer.style.width = '300px';

            document.body.appendChild(mentionContainer);

            // Track the selection before losing focus
            let savedRange: FtQuillRange | null = null;

            quill.on('selection-change', (range) => {
                if (range) {
                    savedRange = range;
                    ftDebugLog('quill mention', { savedRange: range });
                }
            });

            // Fetch mention options
            async function fetchMentions(query: string): Promise<MentionOption[]> {
                // ⭐ THE PAGE'S CONTAINER, when it has one. An article editor knows which world it is
                // in, so @ can offer that world's articles, its placed creatures and their inventory
                // alongside the item catalog.
                //
                // ⛔ ABSENT SCOPE MEANS ITEMS ONLY, never "everything". Most rich-text fields on this
                // site have no world behind them, and the endpoint treats a missing scope as
                // catalog-only for exactly that reason — items are universal, the rest is contained.
                const scope = (window as unknown as { FT_MENTION_SCOPE?: { kind?: string; id?: string } }).FT_MENTION_SCOPE;
                const scopeQuery = scope?.kind && scope?.id
                    ? `&scope_kind=${encodeURIComponent(scope.kind)}&scope_id=${encodeURIComponent(scope.id)}`
                    : '';
                // ⚠ The term is ENCODED. A name with an '&' in it used to truncate the query.
                const response = await fetch(`${BASE_URL}options/search/search_quill_inc.php?query=${encodeURIComponent(query)}${scopeQuery}`);
                const payload: unknown = await response.json();
                return Array.isArray(payload)
                    ? payload.map(mentionOptionOf).filter((mention): mention is MentionOption => mention !== null)
                    : [];
            }

            // Show mention dropdown
            function showMentionDropdown(mentions: MentionOption[], position: { left: number; top: number }) {
                mentionContainer.innerHTML = '';
                mentions.forEach((item) => {
                    const mentionItem = document.createElement('div');
                    mentionItem.className = 'mention-item';
                    mentionItem.style.display = 'flex';
                    mentionItem.style.alignItems = 'center';
                    mentionItem.style.justifyContent = 'space-between';
                    mentionItem.style.padding = '5px 10px';
                    mentionItem.style.cursor = 'pointer';

                    mentionItem.innerHTML = `
                        <span>${item.name} <small style="color: #888;">(${item.label ?? ucwordsAndSpace(item.item_type)})</small></span>
                        <img src="${item.icon}" alt="${item.name}" style="width: 25px; height: 25px; margin-left: auto;">
                    `;

                    mentionItem.addEventListener('click', () => {
                        ftDebugLog('quill mention', { clicked: item?.name, id: item?.id });
                        insertMention(item);
                        mentionContainer.style.display = 'none';
                    });

                    mentionContainer.appendChild(mentionItem);
                });

                mentionContainer.style.left = `${position.left}px`;
                mentionContainer.style.top = `${position.top}px`;
                mentionContainer.style.display = 'block';
            }

            // Insert mention into Quill editor
            function insertMention(item: MentionOption) {
                const range = savedRange || { index: quill.getLength(), length: 0 };

                if (range) {
                    const prefixText = quill.getText(0, range.index);
                    const mentionTriggerIndex = prefixText.lastIndexOf('@');

                    if (mentionTriggerIndex !== -1) {
                        const prefixLength = range.index - mentionTriggerIndex;
                        quill.deleteText(mentionTriggerIndex, prefixLength);
                    }

                    const mentionText = `[${item.name}](${item.item_type}:${item.id})`;
                    quill.insertText(mentionTriggerIndex, mentionText);
                    quill.insertText(mentionTriggerIndex + mentionText.length, ' ');
                    quill.setSelection(mentionTriggerIndex + mentionText.length + 1);
                    quill.focus();
                } else {
                    ftDebugLog('quill mention', { warn: 'No valid range for insertion' });
                }
            }

            // Monitor typing for mentions
            quill.on('text-change', async function (_delta: unknown, _oldDelta: unknown, source: string) {
                if (source !== 'user') return;

                const range = quill.getSelection();
                if (!range) {
                    ftDebugLog('quill mention', { warn: 'No selection range on text change' });
                    return;
                }

                const prefixText = quill.getText(0, range.index);
                const mentionTriggerIndex = prefixText.lastIndexOf('@');
                if (mentionTriggerIndex === -1) {
                    mentionContainer.style.display = 'none';
                    return;
                }

                const query = prefixText.slice(mentionTriggerIndex + 1);
                if (query.length < 1) {
                    mentionContainer.style.display = 'none';
                    return;
                }

                ftDebugLog('quill mention', { fetchQuery: query });
                const mentions = await fetchMentions(query);
                ftDebugLog('quill mention', { mentionCount: mentions?.length });

                const position = quill.getBounds(range.index);
                showMentionDropdown(mentions, {
                    left: position.left + editor.offsetLeft,
                    top: position.top + editor.offsetTop + 30,
                });
            });

            // Hide dropdown on click outside
            document.addEventListener('click', (e) => {
                if (!mentionContainer.contains(e.target as Node)) {
                    mentionContainer.style.display = 'none';
                }
            });
        });

        if (unfocus_inputs) {
            // document.activeElement.blur();
        }

        // console.log('initializeQuillEditors: Complete');
    }
}

window.initializeQuillEditors = initializeQuillEditors;
window.applyQuillCodeBlockRichContentPatch = applyQuillCodeBlockRichContentPatch;

export { applyQuillCodeBlockRichContentPatch, initializeQuillEditors, mentionOptionOf };
